import React, { CSSProperties, Fragment, ReactElement, useCallback, useMemo } from "react";
import { TimelineBarGroupProps } from "./TimelineBarGroup.types";
import { TimelineBar } from "../TimelineBar";
import { calculateBarPosition, formatDates, plannerStyling } from "../utils";
import { CaptionBody, SmallBody } from "src/components/Text";
import { Conditional } from "src/components/Conditional";
import { localize } from "src/l10n";

function TimelineBarGroup({
    axisWidth,
    group,
    onBarClick,
    onBarGroupClick,
    onItemDurationChange,
    timelineDuration,
    timelineMode,
    timelineWidth,
    todayTime,
    color
}: TimelineBarGroupProps): ReactElement {
    const minDate = useMemo(() => group.items.reduce<number>(
        (acc, item) => Math.min(acc, item.start.getTime()),
        Number.MAX_SAFE_INTEGER,), [group]);

    const maxDate = useMemo(() => group.items.reduce<number>(
        (acc, item) => Math.max(acc, item.end.getTime()),
        Number.MIN_SAFE_INTEGER,), [group]);

    const barStyle = useMemo<CSSProperties>(() => {
        if (group.layers < 2) {
            return {};
        }

        const { left, right } = calculateBarPosition(
            timelineWidth,
            timelineDuration,
            minDate,
            maxDate,
        );

        const height = plannerStyling.smallBarHeight;
        const offset = (plannerStyling.categoryRowHeight - height) / 2;

        return {
            height: `${height}px`,
            left: `${left}px`,
            right: `${right}px`,
            top: `${offset}px`,
        };
    }, [minDate, maxDate, timelineDuration, timelineWidth, group.layers]);

    const onBarGroupClicked = useCallback(
        () => onBarGroupClick?.(group),
        [group, onBarGroupClick],
    );

    const title = useMemo(() => {
        return group.items.length + " " + localize("OBJECTS").toLowerCase() + " (" + formatDates(minDate, maxDate) + ")"
    }, [group, minDate, maxDate]);

    if (group.layers < 2) {
        return (
            <Fragment>
                {group.items.map((item) => (
                    <TimelineBar
                        axisWidth={axisWidth}
                        item={item}
                        key={item.key}
                        onBarClick={onBarClick}
                        onItemDurationChange={onItemDurationChange}
                        timelineDuration={timelineDuration}
                        timelineMode={timelineMode}
                        timelineWidth={timelineWidth}
                        todayTime={todayTime}
                        color={color}
                    />
                ))}
            </Fragment>
        );
    }

    return (
        <Fragment>
            <div className="TimelineBarGroup" style={barStyle} title={title}>
                <div
                    className="TimelineBarGroup-inner"
                    onClick={onBarGroupClicked}
                    style={{ backgroundColor: color + "33", }}
                >
                    <SmallBody
                        className="TimelineBarGroup-label-top"
                        lineHeight="low"
                        weight="medium"
                        color="contentDark"
                    >
                        {title}
                    </SmallBody>
                </div>
            </div>
            <Conditional condition={group.expanded}>
                {group.items.map((item) => (
                    <TimelineBar
                        axisWidth={axisWidth}
                        item={item}
                        key={item.key}
                        onBarClick={onBarClick}
                        onItemDurationChange={onItemDurationChange}
                        timelineDuration={timelineDuration}
                        timelineMode={timelineMode}
                        timelineWidth={timelineWidth}
                        todayTime={todayTime}
                        color={color}
                    />
                ))}
            </Conditional>
        </Fragment>
    );
}

export default TimelineBarGroup;
